import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import Formsy from 'formsy-react'

import { Autocomplete, TextField, Button, IconButton, Table, TableContainer, TableHead, TableBody, Paper, 
        DialogTitle, DialogContent, DialogActions, FormGroup, FormControlLabel, Checkbox, 
        Tooltip, Stack, Menu, MenuItem, } from '@mui/material'
import { Add, Delete, Edit, Refresh, FilterAlt, RestoreFromTrash, CheckBoxOutlineBlank, CheckBox, } from '@mui/icons-material'

import _ from 'underscore'

import { C, D, Loading, SearchField, TRow, TCell, TextFieldFormsy, CustomSelect, ConfirmDelete } from '../include'

const oicon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

// ==========================================
// Abre o form de ediçaõ/adição de usuarios
// ==========================================
const UserForm = ({ 
  row = {}, edit, api, alert, dialogClose, existingUsers, loggedUser, editCourt, editSection 
}) => {
  // Estado dos dados do formulário
  const [formData, setFormData] = useState({
    displayName: row?.name || '',
    email: row?.email || '',
    accessProfile: row?.role || '',
    court: row?.court || editCourt || '',
    section: row?.section || editSection || '',
    password: '',
    passwordConfirm: '',
    funcao: row?.funcao || '',
    sideeffect_rd: typeof row?.sideeffect_rd === 'boolean' ? row.sideeffect_rd : true,
    unread_alert: !!row?.unread_alert,
    newdoc_alert: !!row?.newdoc_alert,
    pend_alert: !!row?.pend_alert,
    selectGrupos: [], // Nomes dos grupos selecionados
    grupoId: []       // IDs dos grupos selecionados
  });

  // Estado dos dados dos seletores (tabelas de apoio)
  const [selectors, setSelectors] = useState({
    profiles: [],
    courts: [],
    sections: [],
    grupos: []
  });

  // Fetch de dados de apoio na inicialização
  useEffect(() => {
    let isMounted = true;

    const fetchDependencies = async () => {
      try {
        const isAdmin = loggedUser?.role === 'admin';

        const [profilesResp, courtsResp, sectionsResp, gruposResp] = await Promise.all([
          api.get('/UserProfiles'),
          api.get('/Courts'),
          api.get('/Sections'),
          api.get('/Grupos')
        ]);

        if (!isMounted) return;

        // Filtros de negócio
        const profiles = isAdmin ? profilesResp : profilesResp.filter(p => p.name !== 'admin');
        const courts = isAdmin ? courtsResp : courtsResp.filter(c => c.nome?.toLowerCase() !== 'ai2');
        const sections = isAdmin ? sectionsResp : sectionsResp.filter(s => !s.name?.toLowerCase().includes('ai2'));
        
        const gruposIgnorados = ['fora de operação', 'indeterminado'];
        const grupos = (gruposResp || []).filter(g => {
          const nomeFormatado = g.nome?.trim().toLowerCase();
          return nomeFormatado && !gruposIgnorados.includes(nomeFormatado);
        });

        setSelectors({ profiles, courts, sections, grupos });

        // Se for edição, busca os grupos vinculados ao usuário
        if (edit && row?.id) {
          const gruposDoUsuario = await api.get(`/users/grupos?selectUser=${JSON.stringify([row.id])}`);
          if (!isMounted) return;
          
          setFormData(prev => ({
            ...prev,
            selectGrupos: gruposDoUsuario.map(v => v.nome),
            grupoId: gruposDoUsuario.map(v => v.grupo_id)
          }));
        }
      } catch (err) {
        console.error('Erro ao carregar dependências do formulário de usuário:', err);
      }
    };

    fetchDependencies();
    return () => { isMounted = false; };
  }, [api, edit, loggedUser?.role, row?.id]);

  // Derivações automáticas
  const currentSections = useMemo(() => {
    if (!formData.court) return [];
    const grouped = _.groupBy(selectors.sections, 'court');
    return grouped[formData.court] || [];
  }, [formData.court, selectors.sections]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGruposChange = (selectedNames) => {
    const ids = selectors.grupos
      .filter(g => selectedNames.includes(g.nome))
      .map(g => g.id);
    
    setFormData(prev => ({ ...prev, selectGrupos: selectedNames, grupoId: ids }));
  };

  const handleSave = () => {
    // Validação de E-mail duplicado
    const emailJaExiste = existingUsers?.some(u =>
      u.email.toLowerCase() === formData.email.toLowerCase() &&
      (!edit || u.id !== row?.id)
    );
    
    if (emailJaExiste) {
      alert('O e-mail informado já está cadastrado!');
      return;
    }

    const dt = {
      displayName: formData.displayName,
      email: formData.email,
      password: formData.password,
      'password-confirm': formData.passwordConfirm,
      grupo_id: JSON.stringify(formData.grupoId),
      funcao: formData.funcao,
      sideeffect_rd: formData.sideeffect_rd,
      unread_alert: formData.unread_alert,
      newdoc_alert: formData.newdoc_alert,
      pend_alert: formData.pend_alert,
      accessProfile: {
        label: formData.accessProfile,
        value: selectors.profiles.find(p => p.name === formData.accessProfile)?.id
      },
      section: {
        label: formData.section,
        value: selectors.sections.find(s => s.name === formData.section)?.id
      }
    };

    const apiCall = edit ? api.put('/user', dt) : api.post('/user', dt);

    apiCall
      .then(r => {
        dialogClose();
        alert(r?.msg || "Operação realizada com sucesso.");
      })
      .catch(() => {
        dialogClose();
        alert("Falha na comunicação com o servidor!");
      });
  };

  const canSave = 
    formData.displayName && 
    formData.email && 
    formData.accessProfile && 
    formData.court && 
    formData.section && 
    (!edit 
      ? (formData.password && formData.passwordConfirm)
      : (!formData.password && !formData.passwordConfirm) || (formData.password && formData.passwordConfirm)
    );

  return (
    <Formsy onValidSubmit={() => null}>
      <DialogTitle style={{ minWidth: '400px' }}>{edit ? "Editar usuário" : "Novo usuário"}</DialogTitle>

      <DialogContent>
        <D h="0.5rem" />
        <TextFieldFormsy 
          required type="text" name="displayName" label="Nome" 
          value={formData.displayName} onChange={v => handleChange('displayName', v)}
          validations={{ minLength: 4 }} validationErrors={{ minLength: 'Nome deve ter ao menos 4 letras' }}
        />

        <TextFieldFormsy
          required type="text" name="email" label="E-mail" autoComplete="email"
          value={formData.email} onChange={v => handleChange('email', v)}
          onBlur={() => handleChange('email', formData.email.toLowerCase())}
          validations="isEmail" validationErrors={{ isEmail: 'Email inválido' }}
          disabled={!!edit} validateOnChange={false}
        />

        <D className="row aC">
          <TextFieldFormsy 
            required={!edit} type="password" name="password" label="Nova senha" autoComplete="new-password"
            value={formData.password} onChange={v => handleChange('password', v)}
            validations={{ minLength: 8 }} validationErrors={{ minLength: 'Ao menos 8 caracteres' }}
          />
          <D size="1rem" />
          <TextFieldFormsy 
            required={!edit} type="password" name="passwordConfirm" label="Confirmar senha" autoComplete="new-password"
            value={formData.passwordConfirm} onChange={v => handleChange('passwordConfirm', v)}
            validations="equalsField:password" validationErrors={{ equalsField: 'As senhas são diferentes' }} 
          />
        </D>

        <CustomSelect 
          label="Perfil de acesso *" 
          options={selectors.profiles.map(p => p.name)} 
          value={formData.accessProfile} 
          onChange={v => handleChange('accessProfile', v)} 
        />

        <CustomSelect
          label={`${C.businessLabel} *`}
          options={selectors.courts.map(c => c.nome)} 
          value={formData.court}
          onChange={v => {
            // SOLUÇÃO: Filtramos as seções da nova empresa escolhida 'v' e já injetamos a primeira 
            // no estado, atualizando Empresa e Departamento numa tacada só.
            const newSections = selectors.sections.filter(s => s.court === v);
            
            setFormData(prev => ({ 
              ...prev, 
              court: v, 
              section: newSections[0]?.name || '' // Auto-seleciona a 1ª opção da nova lista
            }));
          }}
        />

        <CustomSelect
          label={`${C.divisionLabel} *`}
          options={currentSections.map(s => s.name)} 
          value={formData.section}
          onChange={v => handleChange('section', v)}
          disabled={!formData.court} 
        />

        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={formData.sideeffect_rd} onChange={e => handleChange('sideeffect_rd', e.target.checked)} />} 
            label="Registra leituras de documentos" 
          />
          <FormControlLabel 
            control={<Checkbox checked={formData.unread_alert} onChange={e => handleChange('unread_alert', e.target.checked)} />} 
            label="Documentos não lidos" 
          />
          <FormControlLabel
            control={<Checkbox checked={formData.newdoc_alert} onChange={e => handleChange('newdoc_alert', e.target.checked)} />} 
            label="Documentos novos" 
          />
          <FormControlLabel 
            control={<Checkbox checked={formData.pend_alert} onChange={e => handleChange('pend_alert', e.target.checked)} />} 
            label="Documentos com pendência" 
          />
        </FormGroup>

        <TextFieldFormsy 
          type="text" name="funcao" label="Função" 
          value={formData.funcao} onChange={v => handleChange('funcao', v)}
        />
        
        <D h="1rem" />
        
        <Autocomplete
          multiple
          options={selectors.grupos.map(g => g.nome)}
          value={formData.selectGrupos}
          getOptionLabel={(option) => option || ''}
          isOptionEqualToValue={(option, value) => option === value}
          onChange={(e, value) => handleGruposChange(value)}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option}>
              <Checkbox icon={oicon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
              {option}
            </li>
          )}
          style={{ width: "100%" }}
          renderInput={(params) => <TextField {...params} label="Filial-Grupo" />}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="primary" onClick={dialogClose}>Cancelar</Button>
        <Button variant="outlined" color="primary" disabled={!canSave} onClick={handleSave}>Salvar</Button>
      </DialogActions>
    </Formsy>
  );
};

  const columnMap = {
    nome: 'name',
    email: 'email',
    perfil: 'role',
    business: 'court',
    division: 'section',
  };

// ==========================================
// COMPONENTE PRINCIPAL: Users
// ==========================================
export const Users = ({ api, user, permission, dialog, dialogClose, alert }) => {
  const [usersData, setUsersData] = useState(null); // null indica loading
  const [searchField, setSearchField] = useState('');
  
  // Excel Filter states
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentColumn, setCurrentColumn] = useState(null);
  const [filters, setFilters] = useState({});

  // useRef para evitar chamadas duplas no mount (React Strict Mode)
  const isFirstRender = useRef(true);

  const fetchUsers = useCallback(() => {
    setUsersData(null); // Trigger loading state
    api.get('/Users').then(data => {
      // Pré-filtro de admin
      const filtered = user?.role !== 'admin' ? data.filter(r => r.role !== 'admin') : data;
      setUsersData(filtered);
    }).catch(err => {
      console.error("Erro ao buscar usuários:", err);
      setUsersData([]);
    });
  }, [api, user?.role]);

  // Efeito responsável pelo mount seguro
  useEffect(() => {
    if (isFirstRender.current) {
      fetchUsers();
      isFirstRender.current = false;
    }
  }, [fetchUsers]);


  // Motor de filtragem de dados
  const filteredUsers = useMemo(() => {
    if (!usersData) return [];

    return usersData.filter(row => {
      // 1. Filtro de Busca (SearchField)
      if (searchField) {
        const searchStr = Object.values(row).map(v => String(v || '')).join('').toLowerCase();
        if (!searchStr.includes(searchField.toLowerCase())) return false;
      }

      // 2. Filtro Excel (Checkbox)
      const keep = Object.entries(filters).every(([col, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) return true;
        
        const key = columnMap[col] || col;
        const cellValue = String(row[key] || '');
        return selectedValues.includes(cellValue);
      });

      return keep;
    });
  }, [usersData, searchField, filters, ]);

  // Valores únicos para popular o dropdown do filtro do Excel
  const currentFilterValues = useMemo(() => {
    if (!currentColumn || !usersData) return [];
    const key = columnMap[currentColumn] || currentColumn;
    const uniqueValues = [...new Set(usersData.map(r => String(r[key] || '')))];
    return uniqueValues.sort((a, b) => a.localeCompare(b, 'pt', { sensitivity: 'base' }));
  }, [currentColumn, usersData, ]);

  // Controles do menu de filtro
  const handleOpenFilter = (e, colName) => {
    setAnchorEl(e.currentTarget);
    setCurrentColumn(colName);
  };
  
  const handleCloseFilter = () => {
    setAnchorEl(null);
    setCurrentColumn(null);
  };

  const handleToggleValue = (val) => {
    const selected = filters[currentColumn] || [];
    const newSelected = selected.includes(val)
      ? selected.filter(v => v !== val)
      : [...selected, val];
    
    setFilters(prev => ({ ...prev, [currentColumn]: newSelected }));
  };

  const openDeleteDialog = (row) => {
    dialog(
      <ConfirmDelete
        title="Confirmar desativação"
        msg="Tem certeza que deseja desabilitar o usuário?"
        itemName={
          <>
            {row.name}
            <br />
            {/* <span style={{ fontSize: '0.9rem', color: '#666' }}> */}
              {row.email}
            {/* </span> */}
          </>
        }
        onCancel={dialogClose}
        onConfirm={() => {
          api.delete(`/User/${row.id}`)
            .then(r => {
              dialogClose();
              fetchUsers(); // Refresh após deletar
              alert(r.msg);
            })
            .catch(e => alert(e.msg || 'Erro ao desabilitar usuário.'));
        }}
      />
    );
  };

  // =======================================================
  // Função atualizada para aplicar limites exatos
  // =======================================================
  const renderFilterHeader = (colName, label, cellSx, divStyle) => (
    <TCell 
      sx={{ 
        whiteSpace: 'normal', 
        wordWrap: 'break-word', 
        overflowWrap: 'break-word',
        ...cellSx // Injeta a largura (width, minWidth) da célula
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', ...divStyle }}>
        {label}
        <IconButton 
          size="small" 
          onClick={(e) => handleOpenFilter(e, colName)} 
          sx={{
            color: filters[colName] && filters[colName].length > 0 ? '#1976d2' : '#fff',
            '&:hover': { color: '#ddd' }, p: 0, ml: 0.5,
          }}
        >
          <FilterAlt fontSize="small" />
        </IconButton>
      </div>
    </TCell>
  );

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      {permission === 'w' && (
        <>
          <D className="row w aF" style={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SearchField
                value={searchField}
                onChange={v => setSearchField(v)}
                style={{ flexShrink: 0, width: '450px' }}
              />
              <Tooltip title="Atualizar" arrow>
                <IconButton onClick={fetchUsers} color="default">
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Stack>
            
            <Button
              variant="contained"
              color="primary"
              onClick={() => dialog(
                <UserForm
                  api={api} alert={alert} existingUsers={usersData} row={null} loggedUser={user}
                  dialogClose={() => { fetchUsers(); dialogClose(); }}
                />
              )}
            >
              <Add />Usuário
            </Button>
          </D>
          <D h="1rem" />
        </>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TRow>
                {permission === 'w' && <TCell sx={{ width: '8ch' }} align="center">Editar</TCell>}
                {permission === 'w' && <TCell sx={{ width: '8ch' }} align="center">Excluir</TCell>}
                <TCell sx={{ width: '8ch' }} align="left">ID</TCell>
                
                {/* Restauradas as travas rígidas de tamanho de coluna */}
                {renderFilterHeader(
                  'nome', `Nome (${filteredUsers.length} usuários)`, 
                  { width: '24ch', minWidth: '24ch' }, { maxWidth: '24ch' }
                )}
                {renderFilterHeader(
                  'email', 'Email', 
                  { width: '24ch' }, { width: '24ch' }
                )}
                {renderFilterHeader(
                  'perfil', 'Perfil de acesso', 
                  { width: '18ch' }, { width: '18ch' }
                )}
                {renderFilterHeader(
                  'business', C.businessLabel, 
                  { width: '18ch' }, { maxWidth: '16ch' }
                )}
                {renderFilterHeader(
                  'division', C.divisionLabel, 
                  { width: '18ch' }, {}
                )}
                
                <TCell sx={{ width: '100%', maxWidth: '45ch' }} align="left">Filial-Grupo</TCell>
              </TRow>
            </TableHead>

            <TableBody>
              {usersData === null ? (
                <TRow><TCell align="center" colSpan={9}><Loading /></TCell></TRow>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map(row => (
                  <TRow key={row.id} row={row} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    {permission === 'w' && (
                      <TCell align="center">
                        <IconButton 
                          style={{ color: '#aaa' }} 
                          onClick={() => dialog(
                            <UserForm 
                              edit row={row} api={api} alert={alert} existingUsers={usersData} loggedUser={user}
                              dialogClose={() => { fetchUsers(); dialogClose(); }} 
                            />
                          )}
                        >
                          {row.inativo ? <RestoreFromTrash /> : <Edit />}
                        </IconButton>
                      </TCell>
                    )}
                    {permission === 'w' && (
                      <TCell align="center">
                        {!row.inativo && (
                          <IconButton 
                            style={{ color: '#aaa' }} 
                            onClick={() => openDeleteDialog(row)}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </TCell>
                    )}
                    <TCell>{row.id}</TCell>

                    <TCell style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {row.name}
                    </TCell>
                    <TCell style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {row.email}
                    </TCell>

                    <TCell>{row.role}</TCell>
                    <TCell>{row.court}</TCell>
                    <TCell>{row.section}</TCell>
                    <TCell style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '40ch' }}>
                      {row.grupos}
                    </TCell>
                  </TRow>
                ))
              ) : (
                <TRow><TCell align="center" colSpan={9}>Nenhum usuário encontrado!</TCell></TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Menu de Filtros (Estilo Excel) */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseFilter}>
        {currentFilterValues.map(v => (
          <MenuItem key={v} onClick={() => handleToggleValue(v)}>
            <Checkbox checked={(filters[currentColumn] || []).includes(v)} size="small" />
            {v || <em>(vazio)</em>}
          </MenuItem>
        ))}
      </Menu>
    </D>
  );
};